/* Mission Control: sponsors in orbit.
   The markup is a plain logo grid (the reduced-motion and no-JS state). When motion is allowed,
   a geometry solver fits one to three tilted rings around the core with keep-out clearance and
   ring spacing measured from the real logo sizes, so marks never overlap each other or the core.
   Hover or focus decelerates the orbit to a stop and lights the target; Pause pins the stop. */
(() => {
  const reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const TILT = (-5 * Math.PI) / 180;
  const GAP = 18;

  // Ramanujan's approximation of an ellipse perimeter
  const perimeter = (a, b) => Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));

  function Orbit(root) {
    const stage = root.querySelector('[data-mc-orbit-stage]');
    const svg = root.querySelector('[data-mc-orbit-rings]');
    const controls = root.querySelector('[data-mc-orbit-controls]');
    const pauseBtn = root.querySelector('[data-mc-orbit-pause]');
    const target = root.querySelector('[data-mc-orbit-target]');
    const sats = [...root.querySelectorAll('.mc-sponsors__sat')];
    if (!stage || sats.length < 3) return;

    let W = 0, H = 0, cx = 0, cy = 0, plan = { speeds: [0.09], dirs: [1] }, geo = [];
    let clock = 0, last = 0, raf = 0, speed = 1;
    let hovering = false, focused = false, pinned = false, grid = false, visible = true;
    let current = null;
    // Slots ride the rings; brands occupy slots. When a ring can't hold every brand (phones),
    // brands waiting in the queue take over a slot as it passes behind the core.
    const all = sats.map((el) => ({ el, w: 0, h: 0, text: !!el.querySelector('.mc-logo--text') }));
    let items = [];
    let queue = [];

    function measure() {
      const rect = stage.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      cx = W / 2;
      cy = H / 2 - 10;
      all.forEach((b) => { b.w = b.el.offsetWidth; b.h = b.el.offsetHeight; });
      const MAXS = 1.12; // largest on-screen scale (near side / lit target)
      const maxW = Math.max(...all.map((b) => b.w)) * MAXS;
      const maxH = Math.max(...all.map((b) => b.h)) * MAXS;
      const core = root.querySelector('.mc-sponsors__core');
      const cr = core ? core.offsetWidth / 2 : 60;
      const gap = 14;
      // Inner ring clears the core on both axes; outer ring stays inside the stage
      const inRx = cr + maxW / 2 + gap, inRy = cr + maxH / 2 + gap;
      const outRx = W / 2 - maxW / 2 - 6, outRy = H / 2 - maxH / 2 - 28;
      // As many rings as fit with a full logo width (x) and height (y) between neighbours
      let n = 1;
      for (let k = 3; k >= 2; k--) {
        const dx = (outRx - inRx) / (k - 1), dy = (outRy - inRy) / (k - 1);
        if (dx >= maxW + gap && dy >= maxH + gap) { n = k; break; }
      }
      const speeds = [0.13, 0.09, 0.065], dirs = [1, -1, 1];
      plan = { speeds: speeds.slice(0, n), dirs: dirs.slice(0, n) };
      geo = [];
      for (let i = 0; i < n; i++) {
        const t = n === 1 ? 1 : i / (n - 1);
        const rx = n === 1 ? Math.max(inRx, outRx) : inRx + (outRx - inRx) * t;
        const ry = n === 1 ? Math.max(inRy, outRy) : inRy + (outRy - inRy) * t;
        geo.push({ rx, ry, p: perimeter(rx, ry) });
      }

      // Capacity: each ring holds only as many brands as fit at their widths plus a gap;
      // brands that don't fit wait in the queue and cycle in behind the core
      items = [];
      let n0 = 0;
      geo.forEach((g, ri) => {
        let used = 0;
        const ring = [];
        while (n0 < all.length) {
          const b = all[n0];
          const need = b.w * MAXS + GAP * 1.5;
          if (used + need > g.p && ring.length >= 3) break;
          ring.push(b); used += need; n0++;
        }
        const slack = Math.max(0, g.p - used) / Math.max(1, ring.length);
        let arc = ri * 0.37 * g.p;
        ring.forEach((b) => {
          const w = b.w * MAXS + GAP * 1.5;
          items.push({ brand: b, ring: ri, phase: ((arc + (w + slack) / 2) / g.p) * Math.PI * 2, prev: null });
          arc += w + slack;
        });
      });
      queue = all.slice(n0);
      all.forEach((b) => b.el.classList.remove('is-queued'));
      queue.forEach((b) => { b.el.classList.add('is-queued'); b.el.style.opacity = '0'; });

      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      const deg = (TILT * 180) / Math.PI;
      svg.innerHTML = geo
        .map((g) => `<ellipse cx="${cx}" cy="${cy}" rx="${g.rx.toFixed(1)}" ry="${g.ry.toFixed(1)}" transform="rotate(${deg} ${cx} ${cy})"/>`)
        .join('');
    }

    const BACK = (3 * Math.PI) / 2; // the far apex, where sin(θ) = -1
    const TWO_PI = Math.PI * 2;
    function place() {
      const cos = Math.cos(TILT), sin = Math.sin(TILT);
      items.forEach((slot) => {
        const g = geo[slot.ring];
        const th = slot.phase + clock * plan.speeds[slot.ring] * plan.dirs[slot.ring];
        // Hand the slot to the next queued brand as it passes behind the core
        const a = ((th % TWO_PI) + TWO_PI) % TWO_PI;
        if (queue.length && slot.prev !== null && slot.brand.el !== current) {
          const crossed = plan.dirs[slot.ring] > 0 ? slot.prev < BACK && a >= BACK : slot.prev > BACK && a <= BACK;
          if (crossed) {
            const out = slot.brand;
            out.el.classList.add('is-queued');
            out.el.style.opacity = '0';
            slot.brand = queue.shift();
            slot.brand.el.classList.remove('is-queued');
            queue.push(out);
          }
        }
        slot.prev = a;
        const it = { el: slot.brand.el, w: slot.brand.w, h: slot.brand.h, text: slot.brand.text };
        const lx = g.rx * Math.cos(th);
        const ly = g.ry * Math.sin(th);
        const x = cx + lx * cos - ly * sin;
        const y = cy + lx * sin + ly * cos;
        const z = Math.sin(th); // +1 nearest the viewer (bottom of the ellipse)
        const depth = (z + 1) / 2;
        const lit = it.el === current;
        const s = lit ? 1.12 : Math.max(it.text ? 0.9 : 0, 0.66 + 0.46 * depth);
        it.el.style.transform = `translate3d(${(x - it.w / 2).toFixed(1)}px, ${(y - it.h / 2).toFixed(1)}px, 0) scale(${s.toFixed(3)})`;
        const floor = W < 750 ? 0.6 : 0.55;
        it.el.style.opacity = lit ? '1' : (floor + (1 - floor) * depth).toFixed(3);
        it.el.style.zIndex = lit ? '400' : z > 0 ? String(100 + Math.round(depth * 100)) : String(Math.round(depth * 40));
        if (lit) {
          target.style.transform = `translate3d(${x.toFixed(1)}px, ${(y + (it.h * s) / 2 + 6).toFixed(1)}px, 0) translateX(-50%)`;
        }
      });
    }

    function frame(now) {
      raf = 0;
      if (grid || !visible) return;
      const dt = Math.min(0.05, (now - (last || now)) / 1000);
      last = now;
      const want = hovering || focused || pinned ? 0 : 1;
      speed += (want - speed) * Math.min(1, dt * 5);
      if (speed < 0.002 && want === 0) speed = 0;
      clock += dt * speed;
      place();
      stage.classList.toggle('is-paused', want === 0);
      raf = requestAnimationFrame(frame);
    }
    const kick = () => { if (!raf && !grid && visible) { last = 0; raf = requestAnimationFrame(frame); } };

    function setTarget(el) {
      current = el;
      all.forEach((b) => b.el.classList.toggle('is-target', b.el === el));
      target.textContent = el ? el.dataset.name || '' : '';
      target.classList.toggle('is-on', !!el);
      place();
    }

    function arm() {
      grid = false;
      stage.classList.add('is-orbit');
      stage.tabIndex = 0;
      requestAnimationFrame(() => { measure(); place(); kick(); });
    }
    function disarm() {
      grid = true;
      stage.classList.remove('is-orbit', 'is-paused');
      stage.removeAttribute('tabindex');
      all.forEach((b) => { b.el.classList.remove('is-queued'); b.el.style.transform = ''; b.el.style.opacity = ''; b.el.style.zIndex = ''; });
      setTarget(null);
    }

    // Hover and focus decelerate the orbit to a stop
    stage.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { hovering = true; kick(); } });
    stage.addEventListener('pointerleave', () => { hovering = false; if (!grid) setTarget(null); kick(); });
    stage.addEventListener('pointerover', (e) => { const sat = e.target.closest('.mc-sponsors__sat'); if (sat && !grid) setTarget(sat); });
    stage.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' || grid) return;
      const sat = e.target.closest('.mc-sponsors__sat');
      if (sat) { hovering = true; setTarget(sat); } else { hovering = false; setTarget(null); }
      kick();
    });
    root.addEventListener('focusin', () => { focused = true; kick(); });
    root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) { focused = false; kick(); } });

    pauseBtn.addEventListener('click', () => {
      pinned = !pinned;
      pauseBtn.setAttribute('aria-pressed', String(pinned));
      const label = pauseBtn.querySelector('[data-label-on]');
      label.textContent = pinned ? label.dataset.labelOff : label.dataset.labelOn;
      kick();
    });
    const remeasure = () => { if (!grid) { measure(); place(); } };
    new ResizeObserver(remeasure).observe(stage);
    sats.forEach((s) => s.querySelector('img')?.addEventListener('load', remeasure));
    new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; kick(); }, { rootMargin: '100px' }).observe(stage);
    document.addEventListener('visibilitychange', () => { visible = !document.hidden; kick(); });
    reducedQuery.addEventListener?.('change', (q) => { if (q.matches) { disarm(); controls.hidden = true; } });

    controls.hidden = false;
    arm();
  }

  function init() {
    if (reducedQuery.matches || document.documentElement.classList.contains('shopify-design-mode')) return;
    document.querySelectorAll('.mc-sponsors').forEach((el) => { if (!el.__mc) { el.__mc = 1; Orbit(el); } });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
